import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  CheckIcon,
  ChevronDownIcon,
  KeyRoundIcon,
  PlugZapIcon,
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ConnectSheet,
  useConnectionCatalog,
  useConnections,
} from '@/features/connections';
import type {
  ConnectionProviderView,
  ConnectionView,
  WorkspaceSecretView,
} from '@/lib/api/generated/model';
import { ListCard } from '@/shared/components/ListCard';
import { PagePlaceholder } from '@/shared/components/PagePlaceholder';

import { isSecretError } from '../errors';
import { useCreateWorkspaceSecret } from '../hooks/useCreateWorkspaceSecret';
import { useUpdateSecretDescription } from '../hooks/useUpdateSecretDescription';
import { useDeleteWorkspaceSecret } from '../hooks/useDeleteWorkspaceSecret';
import { useReplaceWorkspaceSecretValue } from '../hooks/useReplaceWorkspaceSecretValue';
import { useWorkspaceSecrets } from '../hooks/useWorkspaceSecrets';
import {
  filterSecrets,
  groupSecrets,
  secretCounts,
  type SecretKindFilter,
} from '../secretFilters';
import { DeleteSecretDialog } from './DeleteSecretDialog';
import { SecretDescriptionSheet } from './SecretDescriptionSheet';
import { SecretFormSheet } from './SecretFormSheet';
import type { SecretFormMode, SecretPrefill } from './SecretFormSheet';
import { SecretsList } from './SecretsList';
import { SecretsMark } from '@/shared/components/emptyMarks';

/**
 * The workspace's secrets and variables.
 *
 * One list card (spec 229 US3): search and the All / Secrets / Variables /
 * Managed chips in its toolbar, then custom and managed entries as one list
 * in groups, the managed ones headed by the connection that wrote them and
 * editable only through it. Every other state — loading, failed, empty,
 * nothing matching — draws inside the same card; the empty one offers a
 * secondary "Custom secret" and a line to the Connections tab, never the
 * provider tiles the page used to draw.
 *
 * Four states, and the distinction between two of them is the one that matters:
 * a failed load must never render as an empty list. Presented as "you have
 * none", it invites someone to add a key that already exists and then meet a
 * conflict they have no way to explain (FR-003).
 */
export function SecretsPage({
  workspaceId,
  createRequested = false,
  prefill,
  replaceRequested,
  onRequestSpent,
}: {
  workspaceId: string;
  /** The address asked for the create form (spec 129, from the palette). */
  createRequested?: boolean;
  /** What the address asked the create form to hold (spec 240). */
  prefill?: SecretPrefill;
  /** The address asked to replace this key's value (spec 240): the MCP
      server's link, when the key it was asked to set already exists. */
  replaceRequested?: string;
  /** The address's request is honoured: clear it. */
  onRequestSpent?: () => void;
}) {
  const { entries, isPending, loadFailed, refetch } =
    useWorkspaceSecrets(workspaceId);

  const [dialog, setDialog] = useState<SecretFormMode | null>(() =>
    createRequested ? { kind: 'create', prefill } : null,
  );

  // Spend the address's request once honoured, so a reload does not open
  // the form again (the Connections rule). A replace is held here until the
  // entries it names have loaded.
  const [replacing, setReplacing] = useState(replaceRequested);
  // The key an AI tool's link asked about (spec 243), until its form closes:
  // a link always names one, which the palette's bare `?create=true` does not.
  const [askedFor, setAskedFor] = useState(
    () => prefill?.key ?? replaceRequested,
  );
  useEffect(() => {
    if (createRequested || replaceRequested !== undefined) onRequestSpent?.();
  }, [createRequested, replaceRequested, onRequestSpent]);
  const [pendingDelete, setPendingDelete] =
    useState<WorkspaceSecretView | null>(null);
  const [describing, setDescribing] = useState<WorkspaceSecretView | null>(
    null,
  );
  const [query, setQuery] = useState('');
  const [kindFilter, setKindFilter] = useState<SecretKindFilter>('all');

  const { providers } = useConnectionCatalog(workspaceId);
  const { connections } = useConnections(workspaceId);
  const [connecting, setConnecting] = useState<{
    provider: ConnectionProviderView;
    connection?: ConnectionView;
    replaceToken?: boolean;
  } | null>(null);
  // The rows a just-saved connection wrote, marked briefly so the reader sees
  // where "Connect" landed (spec "after save the new rows appear highlighted").
  const [highlight, setHighlight] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    if (highlight.size === 0) return;
    const timer = setTimeout(() => {
      setHighlight(new Set());
    }, 4000);
    return () => {
      clearTimeout(timer);
    };
  }, [highlight]);

  const catalog = providers ?? [];
  const managedKeys = Object.fromEntries(
    catalog.flatMap((provider) =>
      provider.exposed_env.map((key) => [
        key,
        { type: provider.type, displayName: provider.display_name },
      ]),
    ),
  );

  function openConnect(type: string, replaceToken = false) {
    const provider = catalog.find((p) => p.type === type);
    if (!provider) return;
    setDialog(null);
    setConnecting({
      provider,
      connection: connections?.find((c) => c.type === type),
      replaceToken,
    });
  }
  // The form's managed-name hint says "Manage", not "Connect", once the owning
  // provider is connected — it already is, and the button opens Manage.
  const connectedTypes = new Set((connections ?? []).map((c) => c.type));

  const create = useCreateWorkspaceSecret(workspaceId);
  const replace = useReplaceWorkspaceSecretValue(workspaceId);
  const remove = useDeleteWorkspaceSecret(workspaceId);
  const describe = useUpdateSecretDescription(workspaceId);

  // Adjusted during render, as React recommends over an effect: the entry
  // named by the address opens its replace form once the list is in hand. A
  // managed entry changes only through its connection, so that opens instead;
  // a key gone since the link was made is offered as a new one.
  if (replacing !== undefined && entries !== undefined) {
    const entry = entries.find((candidate) => candidate.key === replacing);
    if (!entry) {
      setReplacing(undefined);
      setDialog({ kind: 'create', prefill: { key: replacing } });
    } else if (!entry.managed_by) {
      setReplacing(undefined);
      setDialog({ kind: 'edit', entry });
    } else if (providers !== undefined && connections !== undefined) {
      setReplacing(undefined);
      const provider = providers.find(
        (candidate) => candidate.type === entry.managed_by?.type,
      );
      if (provider) {
        setConnecting({
          provider,
          connection: connections.find((c) => c.type === provider.type),
          replaceToken: true,
        });
      }
    }
  }

  const saving = create.isPending || replace.isPending;
  const saveError = dialog?.kind === 'edit' ? replace.error : create.error;

  const all = entries ?? [];
  // Filtering runs over the array already in hand — one workspace's entries,
  // read in a single query — so narrowing is never a second request.
  const visible = filterSecrets(all, query, kindFilter);
  const groups = groupSecrets(visible, connections);
  const counts = secretCounts(all);
  const needle = query.trim();
  const chipLabel = {
    all: 'All',
    secret: 'Secrets',
    variable: 'Variables',
    managed: 'Managed',
  }[kindFilter];
  const noResults =
    needle === ''
      ? `No ${chipLabel.toLowerCase()} entry here.`
      : `No entry matches “${needle}”${kindFilter === 'all' ? '' : ` among ${chipLabel.toLowerCase()}`}.`;

  function openCreate() {
    create.reset();
    replace.reset();
    setDialog({ kind: 'create' });
  }

  function openEdit(entry: WorkspaceSecretView) {
    create.reset();
    replace.reset();
    setDialog({ kind: 'edit', entry });
  }

  function handleSubmit(input: {
    key: string;
    kind: WorkspaceSecretView['kind'];
    value: string;
    description?: string | null;
  }) {
    if (dialog?.kind === 'edit') {
      replace.mutate(
        { key: dialog.entry.key, value: input.value },
        {
          onSuccess: () => {
            setDialog(null);
            setAskedFor(undefined);
            toast.success(
              savedWords(
                `Replaced the value of ${dialog.entry.key}.`,
                askedFor === dialog.entry.key,
              ),
            );
          },
          // No onError: the dialog stays open showing the typed refusal, with
          // the entered value still in the field.
        },
      );
      return;
    }

    create.mutate(input, {
      onSuccess: () => {
        setDialog(null);
        setAskedFor(undefined);
        toast.success(
          savedWords(`Added ${input.key}.`, askedFor !== undefined),
        );
      },
    });
  }

  function handleDescribe(description: string | null) {
    if (!describing) return;
    const { key } = describing;
    describe.mutate(
      { key, description },
      {
        onSuccess: () => {
          setDescribing(null);
          toast.success(
            description === null
              ? `Removed the description of ${key}.`
              : `Updated the description of ${key}.`,
          );
        },
        // No onError: the sheet stays open showing the typed refusal, with the
        // entered text intact.
      },
    );
  }

  function handleDelete() {
    if (!pendingDelete) return;
    const { key } = pendingDelete;
    remove.mutate(key, {
      onSuccess: () => {
        setPendingDelete(null);
        toast.success(`Deleted ${key}.`);
      },
    });
  }

  function cancelDelete() {
    setPendingDelete(null);
    // A refusal belongs to the delete that drew it; the next one starts clean.
    remove.reset();
  }

  // A refusal keeps the dialog open with the service's reason in it
  // (spec 129 FR-005), rather than closing it and toasting.
  const deleteError =
    remove.error === null
      ? null
      : isSecretError(remove.error)
        ? remove.error.message
        : `${pendingDelete?.key ?? 'It'} could not be deleted.`;

  return (
    <PagePlaceholder
      eyebrow="Workspace"
      title="Secrets"
      description="Values every task in this workspace receives as environment variables. A secret is write-only; a variable stays readable."
      icon={KeyRoundIcon}
      // Unconditional: the page's main action must not move or disappear
      // depending on how much data there is. The workflows list learned this
      // the other way round, when its header control degraded to an unlabelled
      // icon exactly as the page got busier.
      actions={
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              // The page's main action fills a phone's width (PH-03).
              <Button size="sm" className="max-md:w-full">
                Add
                <ChevronDownIcon aria-hidden="true" />
              </Button>
            }
          />
          <DropdownMenuContent align="end">
            {catalog.map((provider) => {
              const connected =
                connections?.some((c) => c.type === provider.type) ?? false;
              return (
                <DropdownMenuItem
                  key={provider.type}
                  disabled={connected}
                  onClick={() => {
                    openConnect(provider.type);
                  }}
                >
                  <PlugZapIcon aria-hidden="true" />
                  {provider.display_name}
                  {connected ? (
                    <CheckIcon aria-label="Connected" className="ml-auto" />
                  ) : null}
                </DropdownMenuItem>
              );
            })}
            {catalog.length > 0 ? <DropdownMenuSeparator /> : null}
            <DropdownMenuItem onClick={openCreate}>
              Custom secret
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      }
    >
      {/* `w-full` on every branch, so the region does not change width as it
          moves between loading, empty and loaded; the card is the one shape
          every state draws in (spec 229 FR-013). */}
      <div data-testid="secrets-region" className="flex w-full flex-col gap-3">
        <ListCard
          title="Secrets and variables"
          count={isPending || loadFailed ? undefined : all.length}
          noun={['entry', 'entries']}
          search={{
            value: query,
            onChange: setQuery,
            placeholder: 'Search by name or description',
            label: 'Search entries',
          }}
          chips={{
            label: 'Show',
            options: [
              { value: 'all', label: 'All', count: counts.all },
              { value: 'secret', label: 'Secrets', count: counts.secret },
              { value: 'variable', label: 'Variables', count: counts.variable },
              { value: 'managed', label: 'Managed', count: counts.managed },
            ],
            value: kindFilter,
            onChange: setKindFilter,
          }}
          shown={visible.length}
          total={all.length}
          state={
            isPending
              ? 'loading'
              : loadFailed
                ? 'failed'
                : all.length === 0
                  ? 'empty'
                  : 'idle'
          }
          failedText="The secrets and variables could not be loaded."
          onRetry={() => void refetch()}
          empty={{
            mark: <SecretsMark />,
            title: 'Nothing defined yet.',
            description:
              'Add a secret for a credential a task needs, or a variable for configuration that stays readable.',
            action: (
              <Button size="sm" variant="outline" onClick={openCreate}>
                Custom secret
              </Button>
            ),
          }}
          noResults={{
            description: noResults,
            onClear: () => {
              setQuery('');
              setKindFilter('all');
            },
          }}
        >
          <SecretsList
            workspaceId={workspaceId}
            groups={groups}
            highlight={highlight}
            onEdit={openEdit}
            onDescribe={(entry) => {
              describe.reset();
              setDescribing(entry);
            }}
            onDelete={setPendingDelete}
            onOpenConnection={(group) => {
              if (group.connectionType) openConnect(group.connectionType);
            }}
          />
        </ListCard>
        {!isPending && !loadFailed && all.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            Connect a provider under{' '}
            <Link
              to="/workspaces/$workspaceId/integrations/connections"
              params={{ workspaceId }}
              className="text-foreground underline underline-offset-4"
            >
              Connections
            </Link>{' '}
            to get its secrets.
          </p>
        ) : null}
      </div>

      {dialog ? (
        <SecretFormSheet
          // Remount per target so the form re-seeds from the entry being
          // edited, rather than an effect writing state after the first render.
          key={dialog.kind === 'edit' ? `edit:${dialog.entry.key}` : 'create'}
          mode={dialog}
          open
          isPending={saving}
          error={saveError}
          existingKeys={all.map((entry) => entry.key)}
          managedKeys={managedKeys}
          connectedTypes={connectedTypes}
          onConnect={openConnect}
          onSubmit={handleSubmit}
          onClearError={dialog.kind === 'edit' ? replace.reset : create.reset}
          onCancel={() => {
            setDialog(null);
            setAskedFor(undefined);
          }}
          askedByAiTool={
            askedFor !== undefined &&
            (dialog.kind === 'create' || dialog.entry.key === askedFor)
          }
        />
      ) : null}

      {describing ? (
        <SecretDescriptionSheet
          // Remount per target, so the field re-seeds from the entry being
          // described rather than an effect writing state after first render.
          key={`describe:${describing.key}`}
          entry={describing}
          open
          isPending={describe.isPending}
          error={describe.error}
          onSubmit={handleDescribe}
          onClearError={describe.reset}
          onCancel={() => {
            setDescribing(null);
          }}
        />
      ) : null}

      {pendingDelete ? (
        <DeleteSecretDialog
          entryKey={pendingDelete.key}
          open
          isPending={remove.isPending}
          onConfirm={handleDelete}
          error={deleteError}
          onCancel={cancelDelete}
        />
      ) : null}
      {connecting ? (
        <ConnectSheet
          key={`${connecting.provider.type}:${connecting.connection?.id ?? 'new'}`}
          workspaceId={workspaceId}
          provider={connecting.provider}
          connection={connecting.connection}
          replaceToken={connecting.replaceToken}
          open
          onClose={() => {
            setConnecting(null);
          }}
          onSaved={(connection) => {
            setHighlight(new Set(connection.exposed_env));
          }}
        />
      ) : null}
    </PagePlaceholder>
  );
}

/** A save the AI tool is waiting on says that the tool carries on (spec 243). */
function savedWords(words: string, askedByAiTool: boolean): string {
  return askedByAiTool ? `${words} Your AI tool carries on by itself.` : words;
}
